<script setup>
import { GridItem, GridLayout } from 'grid-layout-plus';
import { cloneDeep, isNil } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const { route } = useCommonImports();

const bi_store = useBiStore();
const { current_grid_layout, scope, selected_dashboard } = storeToRefs(bi_store);

const state = reactive({
  is_dragging: false,
});

const is_grid_responsive = computed(() => {
  return scope.value === 'micro_app' && !route.query.print;
});

let scrollInterval = null;
const SCROLL_MARGIN = 250;
const SCROLL_SPEED = 30;

function handleDragMove(e) {
  if (!props.isEditing || !state.is_dragging)
    return;

  const mouseY = e.clientY;
  const viewportHeight = window.innerHeight;
  const scrollableElement = document.documentElement || document.body;

  if (scrollInterval) {
    clearInterval(scrollInterval);
    scrollInterval = null;
  }

  if (mouseY < SCROLL_MARGIN) {
    const intensity = 1 - (mouseY / SCROLL_MARGIN);
    scrollInterval = setInterval(() => {
      scrollableElement.scrollTop -= SCROLL_SPEED * intensity;
    }, 16);
  }
  else if (mouseY > viewportHeight - SCROLL_MARGIN) {
    const intensity = 1 - ((viewportHeight - mouseY) / SCROLL_MARGIN);
    scrollInterval = setInterval(() => {
      scrollableElement.scrollTop += SCROLL_SPEED * intensity;
    }, 16);
  }
}

function handleDragEnd() {
  if (scrollInterval) {
    clearInterval(scrollInterval);
    scrollInterval = null;
  }
}

function setDragState(bool) {
  state.is_dragging = bool;
}

onMounted(() => {
  document.addEventListener('mousemove', handleDragMove);
  document.addEventListener('mouseup', handleDragEnd);
});

onUnmounted(() => {
  document.removeEventListener('mousemove', handleDragMove);
  document.removeEventListener('mouseup', handleDragEnd);
  if (scrollInterval) {
    clearInterval(scrollInterval);
  }
});

// NOTE: If any logic is changed here, we need to update the logic for quick filters as well.
function applyGlobalFilters(widget) {
  const filters = bi_store.get_global_filters_with_applied_filters;
  const item = cloneDeep(widget);
  filters.forEach((filter) => {
    if (filter.widgets[item.uid]) {
      const widget_config = filter.widgets[item.uid];
      if (filter.general.filter_type === 'date_interval') {
        const columns = item.config.query.stages[widget_config.stage_index].columns;
        const selected_column = columns.find(column => column.alias === widget_config.alias);
        if (selected_column) {
          selected_column.dateBin = filter.applied_filter_config.value;
        }
      }
      else if (!isNil(widget_config.stage_index) && item.config.query.stages[widget_config.stage_index].filters) {
        const new_filter = {
          ...filter.applied_filter_config,
          field: widget_config.field,
        };
        if (widget_config.override) {
          const index = item.config.query.stages[widget_config.stage_index].filters.findIndex(filter => filter.field === widget_config.field);
          if (index !== -1)
            item.config.query.stages[widget_config.stage_index].filters[index] = new_filter;
        }
        else {
          item.config.query.stages[widget_config.stage_index].filters.push(new_filter);
        }
      }
    }
  });
  return item.config;
}
</script>

<template>
  <GridLayout
    :key="selected_dashboard"
    v-model:layout="current_grid_layout"
    :row-height="30"
    :class="props.isEditing ? 'active-grid' : ''"
    :is-draggable="props.isEditing"
    :is-resizable="props.isEditing"
    :responsive="is_grid_responsive"
    @layout-updated="setDragState(false)"
  >
    <GridItem
      v-for="item in current_grid_layout"
      :key="item.i"
      :x="item.x"
      :y="item.y"
      :w="item.w"
      :h="item.h"
      :i="item.i"
      drag-ignore-from=".ignore-widget-drag"
      :uid="item.uid"
      @resize="setDragState(true)"
      @move="setDragState(true)"
    >
      <BiWidget
        :key="item.uid"
        :is-editing="props.isEditing"
        :widget-id="item.uid"
        :widget-name="item.name"
        :config="applyGlobalFilters(item)"
        :original-config="item.config"
        :class="props.isEditing ? 'select-none' : ''"
      />
    </GridItem>
  </GridLayout>
  <div v-if="props.isEditing" class="h-6" />
</template>

<style scoped>
.vgl-layout {
  @apply bg-gray-50;
}

.vgl-layout::before {
  position: absolute;
  width: calc(100% - 5px);
  height: calc(100% - 5px);
  margin: 5px;
  content: '';
  background-repeat: repeat;
  background-size: calc(calc(100% - 5px) / 12) 40px;
}

:deep(.vgl-item:not(.vgl-item--placeholder)) {
  @apply relative;
}

.active-grid :deep(.vgl-item:not(.vgl-item--placeholder)):hover {
  @apply border-transparent;
}

.active-grid :deep(.vgl-item:not(.vgl-item--placeholder)):hover::after {
  content: '';
  @apply absolute inset-[-2px] border-2 border-dashed border-primary-600 rounded pointer-events-none;
}

:deep(.vgl-item__resizer) {
  visibility: hidden;
}
:deep(.vgl-item:hover .vgl-item__resizer) {
  visibility: visible;
}

:deep(.vgl-item__resizer::before) {
  border: 0 solid #1570ef;
  border-right-width: 4px;
  border-bottom-width: 4px;
  right: -5px;
  bottom: -5px;
  @apply rounded-br;
}

:deep(.vgl-item__resizer) {
  @apply right-[2px] bottom-[2px] w-3 h-3;
}

:deep(.vgl-item--resizing) {
  opacity: 90%;
}

:deep(.vgl-item--static) {
  background-color: #cce;
}

.text {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: auto;
  font-size: 24px;
  text-align: center;
}

.active-grid {
  background-size: 22.18px 17.7px;
  background-image: conic-gradient(transparent 0deg, transparent 90deg, white 90deg, white 180deg, transparent 180deg, transparent 270deg, white 270deg);
  background-clip: padding-box;
  min-height: 25rem;
}
</style>
